import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, CalendarDays, CheckCircle2, ClipboardList, FileSpreadsheet, RefreshCw, Search, TrendingUp, WalletCards } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import StoreLayout from "./StoreLayout";
import { preferredStoreLocationId, rememberedStoreLocationId } from "./storeLocationPreference";
import {
  getCurrentStoreUser,
  getStoreReports,
  exportStoreReports,
  logoutStore,
  StoreApiError,
  StoreReport,
  StoreReportAmountRow,
  StoreReportCountRow,
  StoreReportDailyOrder,
  StoreReportMetrics,
  StoreUser,
} from "./api";

const now = new Date();
const currentYear = now.getFullYear();
const defaultDateFrom = `${currentYear}-01-01`;
const defaultDateTo = `${currentYear}-12-31`;

function formatMoney(value?: number | null) {
  return `NT$ ${Number(value || 0).toLocaleString("zh-TW")}`;
}

function formatCount(value?: number | null, unit = "筆") {
  return `${Number(value || 0).toLocaleString("zh-TW")} ${unit}`;
}

function maxValue(values: number[]) {
  return Math.max(1, ...values.map((value) => Number(value || 0)));
}

function MetricCard({
  icon,
  label,
  value,
  note,
  tone = "default",
  href,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  note?: string;
  tone?: "default" | "highlight" | "warning";
  href?: string;
}) {
  const toneClass =
    tone === "highlight"
      ? "border-emerald-200 bg-emerald-50"
      : tone === "warning"
        ? "border-amber-200 bg-amber-50"
        : "border-slate-200 bg-white";

  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-500">{label}</p>
          <p className="mt-2 text-2xl font-black text-emerald-950">{value}</p>
        </div>
        <div className="shrink-0 text-emerald-800">{icon}</div>
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        {note ? <p className="min-w-0 text-sm font-semibold leading-relaxed text-slate-500">{note}</p> : <span />}
        {href ? <span className="shrink-0 text-xs font-black text-emerald-800">查看</span> : null}
      </div>
    </>
  );

  if (href) {
    return (
      <Link className={`block rounded-lg border p-4 transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_10px_24px_rgba(16,78,56,.08)] ${toneClass}`} to={href}>
        {content}
      </Link>
    );
  }

  return <div className={`rounded-lg border p-4 ${toneClass}`}>{content}</div>;
}

function Section({ title, children, empty }: { title: string; children: ReactNode; empty?: boolean }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-lg font-black text-emerald-950">{title}</h2>
      </div>
      {empty ? <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center font-semibold text-slate-500">目前區間沒有資料</div> : children}
    </section>
  );
}

function AmountRows({ rows, amountKey }: { rows: StoreReportAmountRow[]; amountKey: "Amount" | "TotalAmount" }) {
  const max = maxValue(rows.map((row) => Number(row[amountKey] || 0)));
  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const amount = Number(row[amountKey] || 0);
        return (
          <div key={row.Key} className="min-w-0">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0 break-words font-black text-emerald-950">{row.Label}</span>
              <span className="shrink-0 font-bold text-slate-600">{formatCount(row.Count)}</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-emerald-700" style={{ width: `${Math.max(8, Math.round((amount / max) * 100))}%` }} />
            </div>
            <p className="mt-1 text-sm font-bold text-slate-500">{formatMoney(amount)}</p>
          </div>
        );
      })}
    </div>
  );
}

function CountRows({ rows }: { rows: StoreReportCountRow[] }) {
  const max = maxValue(rows.map((row) => row.Count));
  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.Key}>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0 break-words font-black text-emerald-950">{row.Label}</span>
            <span className="shrink-0 font-bold text-slate-600">{formatCount(row.Count)}</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emerald-700" style={{ width: `${Math.max(8, Math.round((row.Count / max) * 100))}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function DailyOrderTable({ rows }: { rows: StoreReportDailyOrder[] }) {
  const totalMax = maxValue(rows.map((row) => row.TotalAmount));
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[680px] w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-sm font-black text-slate-600">
            <th className="px-3 py-3">日期</th>
            <th className="px-3 py-3">訂單</th>
            <th className="px-3 py-3">成交總額</th>
            <th className="px-3 py-3">已收金額</th>
            <th className="px-3 py-3">走勢</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.Date} className="border-b border-slate-100 align-middle text-sm font-semibold text-slate-700">
              <td className="px-3 py-3 font-black text-emerald-950">{row.Date}</td>
              <td className="px-3 py-3">{formatCount(row.OrderCount)}</td>
              <td className="px-3 py-3">{formatMoney(row.TotalAmount)}</td>
              <td className="px-3 py-3">{formatMoney(row.PaidAmount)}</td>
              <td className="px-3 py-3">
                <div className="h-2 min-w-[120px] overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-emerald-700" style={{ width: `${Math.max(8, Math.round((row.TotalAmount / totalMax) * 100))}%` }} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummaryMetrics({ dateFrom, dateTo, metrics }: { dateFrom: string; dateTo: string; metrics: StoreReportMetrics }) {
  const orderLink = (params: Record<string, string>) => {
    const query = new URLSearchParams({ DateFrom: dateFrom, DateTo: dateTo, ...params });
    return `/store/orders?${query.toString()}`;
  };
  const serviceLink = `/store/service-records?${new URLSearchParams({ DateFrom: dateFrom, DateTo: dateTo, RecordStatus: "Active" }).toString()}`;
  const followUpOpenLink = `/store/follow-ups?${new URLSearchParams({ DateFrom: dateFrom, DateTo: dateTo, status: "Open" }).toString()}`;
  const followUpCompletedLink = `/store/follow-ups?${new URLSearchParams({ DateFrom: dateFrom, DateTo: dateTo, status: "Completed" }).toString()}`;

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard href={orderLink({ Status: "All" })} icon={<TrendingUp size={22} />} label="成交總額" value={formatMoney(metrics.TotalAmount)} note={`平均客單 ${formatMoney(metrics.AverageOrderAmount)}`} tone="highlight" />
      <MetricCard href={orderLink({ Status: "All" })} icon={<WalletCards size={22} />} label="本期實收" value={formatMoney(metrics.PaymentReceived)} note={`帳面已收 ${formatMoney(metrics.PaidAmount)}`} />
      <MetricCard href={orderLink({ PaymentStatus: "BalanceDue" })} icon={<CalendarDays size={22} />} label="尾款待收" value={formatMoney(metrics.BalanceAmount)} note={`${formatCount(metrics.BalanceDueOrders)}尚未結清`} tone={metrics.BalanceAmount > 0 ? "warning" : "default"} />
      <MetricCard href={orderLink({ Status: "All" })} icon={<ClipboardList size={22} />} label="訂單總數" value={formatCount(metrics.OrderCount)} note={`已交付 ${formatCount(metrics.CompletedOrders)}，作廢/取消 ${formatCount(metrics.CancelledOrders)}`} />
      <MetricCard href={orderLink({ Status: "Deliverable" })} icon={<CheckCircle2 size={22} />} label="可交付流程" value={formatCount(metrics.DeliverableOrders)} note="已到店或可通知取件" />
      <MetricCard href={serviceLink} icon={<ClipboardList size={22} />} label="服務紀錄" value={formatCount(metrics.ServiceRecordCount)} note="依服務日期統計" />
      <MetricCard href={followUpOpenLink} icon={<CalendarDays size={22} />} label="回訪建立" value={formatCount(metrics.FollowUpCreated)} note="依建立時間統計" />
      <MetricCard href={followUpCompletedLink} icon={<CheckCircle2 size={22} />} label="回訪完成率" value={`${metrics.FollowUpCompletionRate || 0}%`} note={`完成 ${formatCount(metrics.FollowUpCompleted)}`} />
    </div>
  );
}

export default function StoreReports() {
  const navigate = useNavigate();
  const [user, setUser] = useState<StoreUser | null>(null);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(() => rememberedStoreLocationId());
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [report, setReport] = useState<StoreReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [exporting, setExporting] = useState(false);

  const effectiveLocationId = useMemo(() => preferredStoreLocationId(user, currentLocationId), [currentLocationId, user]);

  const loadReport = useCallback(async (locationId: string | null, from: string, to: string) => {
    if (!locationId) return;
    setLoading(true);
    setMessage("");
    try {
      const data = await getStoreReports({ LocationId: locationId, DateFrom: from, DateTo: to });
      setReport(data);
    } catch (error) {
      if (error instanceof StoreApiError && error.status === 401) {
        navigate("/store-login");
        return;
      }
      setMessage(error instanceof Error ? error.message : "報表讀取失敗，請稍後再試。");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await getCurrentStoreUser();
        if (!mounted) return;
        setUser(me.User);
        const firstLocationId = preferredStoreLocationId(me.User, currentLocationId);
        setCurrentLocationId(firstLocationId);
        if (firstLocationId) {
          await loadReport(firstLocationId, defaultDateFrom, defaultDateTo);
        } else {
          setLoading(false);
          setMessage("此帳號尚未綁定門市。");
        }
      } catch {
        navigate("/store-login");
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadReport, navigate]);

  const handleLocationChange = (nextLocationId: string) => {
    setCurrentLocationId(nextLocationId);
    void loadReport(nextLocationId, dateFrom, dateTo);
  };

  const handleLogout = async () => {
    await logoutStore();
    navigate("/store-login");
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void loadReport(effectiveLocationId, dateFrom, dateTo);
  };

  const handleReset = () => {
    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
    void loadReport(effectiveLocationId, defaultDateFrom, defaultDateTo);
  };

  const handleExport = async () => {
    setExporting(true);
    setMessage("");
    try {
      await exportStoreReports({ LocationId: effectiveLocationId, DateFrom: report?.DateFrom || dateFrom, DateTo: report?.DateTo || dateTo });
    } catch (error) {
      if (error instanceof StoreApiError && error.status === 401) {
        navigate("/store-login");
        return;
      }
      setMessage(error instanceof Error ? error.message : "匯出失敗，請稍後再試。");
    } finally {
      setExporting(false);
    }
  };

  return (
    <StoreLayout currentLocationId={effectiveLocationId} onLocationChange={handleLocationChange} onLogout={handleLogout} title="門市報表" user={user}>
      <div className="space-y-5">
        <form onSubmit={handleSubmit} className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-[minmax(180px,240px)_minmax(180px,240px)_auto] 2xl:items-end">
            <label className="min-w-0">
              <span className="mb-2 block text-sm font-black text-slate-600">起日</span>
              <input className="h-12 w-full min-w-0 rounded-lg border border-slate-300 px-4 text-base font-semibold text-emerald-950 outline-none focus:border-emerald-700" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
            </label>
            <label className="min-w-0">
              <span className="mb-2 block text-sm font-black text-slate-600">迄日</span>
              <input className="h-12 w-full min-w-0 rounded-lg border border-slate-300 px-4 text-base font-semibold text-emerald-950 outline-none focus:border-emerald-700" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
            </label>
            <div className="rwd-action-grid sm:col-span-2 2xl:col-span-1">
              <button className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-emerald-800 px-5 text-base font-black text-white shadow-sm transition hover:bg-emerald-900 disabled:opacity-60" type="submit" disabled={loading}>
                <Search size={18} />
                查詢
              </button>
              <button className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-base font-black text-emerald-900 transition hover:bg-slate-50 disabled:opacity-60" type="button" onClick={handleReset} disabled={loading}>
                <RefreshCw size={18} />
                重整
              </button>
              <button className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-base font-black text-emerald-900 transition hover:bg-slate-50 disabled:opacity-60" type="button" onClick={handleExport} disabled={loading || exporting || !effectiveLocationId}>
                <FileSpreadsheet size={18} />
                {exporting ? "匯出中" : "匯出 Excel"}
              </button>
            </div>
          </div>
        </form>

        {message ? <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 font-bold text-amber-800">{message}</div> : null}

        {loading && !report ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center font-bold text-slate-500">報表載入中...</div>
        ) : report ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-slate-500">統計區間</p>
                <h2 className="text-xl font-black text-emerald-950">
                  {report.DateFrom} 至 {report.DateTo}
                </h2>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-black text-emerald-800">
                <BarChart3 size={18} />
                門市營運摘要
              </div>
            </div>

            <SummaryMetrics dateFrom={report.DateFrom} dateTo={report.DateTo} metrics={report.Metrics} />

            <div className="grid gap-5 2xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
              <Section title="每日訂單走勢" empty={report.DailyOrders.length === 0}>
                <DailyOrderTable rows={report.DailyOrders} />
              </Section>
              <Section title="收款方式" empty={report.PaymentMethods.length === 0}>
                <AmountRows rows={report.PaymentMethods} amountKey="Amount" />
              </Section>
            </div>

            <div className="grid gap-5 lg:grid-cols-3">
              <Section title="訂單狀態" empty={report.OrderStatuses.length === 0}>
                <AmountRows rows={report.OrderStatuses} amountKey="TotalAmount" />
              </Section>
              <Section title="服務類型" empty={report.ServiceTypes.length === 0}>
                <CountRows rows={report.ServiceTypes} />
              </Section>
              <Section title="回訪狀態" empty={report.FollowUpStatus.length === 0}>
                <CountRows rows={report.FollowUpStatus} />
              </Section>
            </div>
          </>
        ) : null}
      </div>
    </StoreLayout>
  );
}
